import * as React from 'react';
const { connect } = require('react-redux');
import { translate } from 'react-i18next';
import { browserHistory, InjectedRouter } from 'react-router';
import { IStore } from 'redux/IStore';
import { Field, reduxForm, InjectedFormProps, change, getFormSyncErrors } from 'redux-form';
import { getUserProfile, putUserProfile, deactivateUser } from 'modules/user/userprofile';
import { ICountryCallingCodeDomainValue, IDropdownData, IImageData } from 'models/common';
import * as c from 'models/constants';
import { IUserProfile, IUserProfileAction, IUserProfileData } from 'models/user/userprofile';
import { PortalDatePicker, PortalInputField, PortalSelectMenu, PortalTextareaField } from 'components/FormControls';
import { ErrorSummary, Button, LoadingOverlayWrapper } from 'components';
import { maxLength, phoneNumber, required } from 'helpers/validationattributes';
import { IModalAction } from 'models/modal';
import { closeModal, showModal } from 'redux/modules/modal';
import { actions as notifActions } from 'redux-notifications';
import { PortalPageSection } from 'components/PortalStructure';
import { preventRouterLeave } from 'helpers/hooks';
import { ManageUserWrapper } from 'containers';
import { Gender } from 'models/enums/gender';
import renderIcon from 'helpers/renderIcon';
const { notifSend } = notifActions;
require('./style.css');

import { IUserPreferences, IUserPreferencesAction } from 'models/user/userpreferences';
import { IRadioButtonOption } from 'models/common/radiobuttonoption';
import { sendVerificationEmail } from 'redux/modules/user/userpreferences';
import moment = require('moment');
import { IOnboardingQuizFormData } from 'models/onboardingquiz';
import { BasicButton } from 'components/Common/Button';
import { createDomainValueOptions, fullPhoneNumber } from 'helpers/common';
import { domainValuesMap } from 'redux/modules/domainvalues';

interface IProps extends InjectedFormProps {
	userProfile: IUserProfileData;
	userPreferences?: IUserPreferences;
	initialValues: IUserProfile;
	currentFormValues: IUserProfile;
	location: any;
	baseUrlClient?: string;
	router: InjectedRouter;
	route: any;
	countryCallingCodes?: ICountryCallingCodeDomainValue[];

	/* form props */
	pristine: boolean;
	dirty: boolean;
	submitting: boolean;
	handleSubmit: any;
	submitFailed: boolean;
	formErrors?: any;

	/* functions props */
	t?: (key: string, props?: any) => string;
	change: (field: string, data: any) => void;
	getUserProfile: () => IUserProfileAction;
	putUserProfile: (user: IUserProfile) => IUserProfileAction;
	showModal?: (modalType: string, modalProps: any) => IModalAction;
	closeModal?: () => IModalAction;
	deactivateUser?: () => void;
	notifSend?: (data: any) => void;
	sendVerificationEmail?: () => IUserPreferencesAction;
	getCountryCallingCodeDomainValues?: () => any;
	reset: () => void;
}

interface IState {
	readOnlyBio: boolean;
	readOnlyProfile: boolean;
}

const maxLength250 = maxLength(250);
const maxLength100 = maxLength(100);
const maxLength50 = maxLength(50);

const modalToFieldsMap = {
	HairLength: ['hairLength'],
	HairColor: ['hairColor', 'coloredHair'],
	HairType: ['hairType'],
	ScalpCondition: ['scalpMoisture', 'hairProducts'],
	ProductBrand: ['brands'],
	HairRelatedGoal: ['goals'],
	SkinProblem: ['skinProblems'],
	EyeColor: ['eyesColor', 'eyebrowType'],
	NailsShape: ['nailShape'],
};

@preventRouterLeave()
@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class UserProfileForm extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.onSubmit = this.onSubmit.bind(this);
		this.onSubmitModal = this.onSubmitModal.bind(this);
		this.imageUploadedCallback = this.imageUploadedCallback.bind(this);
		this.cancelClick = this.cancelClick.bind(this);
		this.deleteImage = this.deleteImage.bind(this);
		this.openDeactivateUser = this.openDeactivateUser.bind(this);
		this.sendVerificationEmail = this.sendVerificationEmail.bind(this);
		this.toggleReadOnlyBio = this.toggleReadOnlyBio.bind(this);
		this.toggleReadOnlyProfile = this.toggleReadOnlyProfile.bind(this);
		this.openModal = this.openModal.bind(this);

		this.state = {
			readOnlyBio: true,
			readOnlyProfile: true,
		};
	}

	public componentDidMount() {
		this.props.getUserProfile();
		this.props.getCountryCallingCodeDomainValues();
		const passwordChanged = this.props.location.query.passwordchanged;
		if (passwordChanged) {
			this.props.notifSend({
				message: this.props.t('Notification_Success_Password_Changed'),
				kind: 'success',
				dismissAfter: 3000,
			});
		}
	}

	public onSubmitModal(form: IOnboardingQuizFormData, type: keyof typeof modalToFieldsMap) {
		const modifiedForm = {
			...this.props.userProfile.user,
		};
		for (const field of modalToFieldsMap[type]) {
			modifiedForm[field] = form[field];
		}
		this.props.putUserProfile(modifiedForm);
	}

	public onSubmit(form: IUserProfile) {
		const modifiedForm = { ...form, bio: this.props.userProfile.user.bio };
		this.props.putUserProfile(modifiedForm);
		this.setState({
			readOnlyBio: true,
			readOnlyProfile: true,
		});
	}

	private cancelClick() {
		browserHistory.push('/');
	}

	private toggleReadOnlyBio() {
		this.setState((s) => {
			this.props.reset();
			return {
				readOnlyBio: !s.readOnlyBio,
				readOnlyProfile: true,
			};
		});
	}

	private toggleReadOnlyProfile() {
		this.setState((s) => {
			this.props.reset();
			return {
				readOnlyProfile: !s.readOnlyProfile,
				readOnlyBio: true,
			};
		});
	}

	private imageUploadedCallback(id: string, href: string, additionalData: string[]) {
		const image: IImageData = {
			id,
			href,
		};

		if (additionalData.length > 0) {
			this.props.change(additionalData[0], image.href);
			this.props.change(additionalData[1], image.id);
		}

		this.forceUpdate();
	}

	private deleteImage(_, additionalData: string[]) {
		this.props.change(additionalData[0], null);
		this.props.change(additionalData[1], null);
		this.forceUpdate();
	}

	private mapErrorKeyToMessage(key: string, t: (key: string) => string) {
		switch (key) {
			case 'givenName':
				return t('User_UserProfile_Label_GivenName');
			case 'familyName':
				return t('User_UserProfile_Label_FamilyName');
			case 'website':
				return t('User_UserProfile_Label_Website');
			case 'location':
				return t('User_UserProfile_Label_Location');
			default:
				return key;
		}
	}

	private openDeactivateUser(e: React.SyntheticEvent) {
		e.preventDefault();
		this.props.showModal('DeactivateUser', {
			closeModal: this.props.closeModal,
			deactivateUser: this.props.deactivateUser,
			baseUrlClient: this.props.baseUrlClient,
		});
	}

	private sendVerificationEmail(e: React.SyntheticEvent) {
		e.preventDefault();
		this.props.sendVerificationEmail();
	}

	private renderReadOnlyBio() {
		const { t, currentFormValues } = this.props;

		const genderOptions: IRadioButtonOption[] = [
			{
				label: t('User_UserProfile_Label_Gender_Male'),
				value: Gender.Male.toString(),
			},
			{
				label: t('User_UserProfile_Label_Gender_Female'),
				value: Gender.Female.toString(),
			},
			{
				label: t('User_UserProfile_Label_Gender_Unspecified'),
				value: Gender.Unspecified.toString(),
			},
		];

		const gender = genderOptions.find((e) => e.value == currentFormValues?.gender);

		const birthdate = currentFormValues?.birthDate;

		return (
			<dl className="list">
				<dt>{t('User_UserProfile_Label_GivenName')}</dt>
				<dd>{currentFormValues?.givenName}</dd>
				<dt>{t('User_UserProfile_Label_FamilyName')}</dt>
				<dd>{currentFormValues?.familyName}</dd>
				<dt>{t('User_UserProfile_Label_PhoneNumber')}</dt>
				<dd>
					{currentFormValues?.callingCodeKey &&
						currentFormValues?.phoneNumber &&
						fullPhoneNumber(t, currentFormValues?.callingCodeKey, currentFormValues?.phoneNumber)}
				</dd>
				<dt>{t('User_UserProfile_Label_Birthday')}</dt>
				<dd>{birthdate ? moment(birthdate).format('L') : ''}</dd>
				<dt>{t('User_UserProfile_Label_Gender')}</dt>
				<dd>{gender?.label}</dd>
			</dl>
		);
	}

	private renderEditableBio() {
		const { t, countryCallingCodes } = this.props;

		const callingCodes = createDomainValueOptions(t, 'DV_CountryCallingCodes_', countryCallingCodes);

		const genderOptions: IDropdownData[] = [
			{
				value: t('User_UserProfile_Label_Gender_Male'),
				key: Gender.Male.toString(),
			},
			{
				value: t('User_UserProfile_Label_Gender_Female'),
				key: Gender.Female.toString(),
			},
			{
				value: t('User_UserProfile_Label_Gender_Unspecified'),
				key: Gender.Unspecified.toString(),
			},
		];

		return (
			<>
				<Field
					name="givenName"
					component={PortalInputField}
					type="text"
					validate={[maxLength50, required]}
					label={t('User_UserProfile_Label_GivenName')}
					description={t('User_UserProfile_Desc_RealName')}
				/>
				<Field
					name="familyName"
					component={PortalInputField}
					type="text"
					validate={[maxLength100, required]}
					label={t('User_UserProfile_Label_FamilyName')}
				/>
				<div className="row">
					<div className="col-md-3">
						<Field
							name={'callingCodeKey'}
							component={PortalSelectMenu}
							type="text"
							items={callingCodes}
							withImages={true}
							multi={false}
							validate={[required]}
							isRequired={true}
							label={t('BSPortal_Common_Phone_Prefix')}
						/>
					</div>
					<div className="col-md-9">
						<Field
							name={'phoneNumber'}
							component={PortalInputField}
							type="text"
							label={t('PhoneNumber_Label')}
							validate={[required, phoneNumber]}
						/>
					</div>
				</div>
				<Field
					name="birthDate"
					component={PortalDatePicker}
					showMonthDropdown={true}
					showYearDropdown={true}
					type="text"
					label={t('User_UserProfile_Label_Birthday')}
					setMaxDate={true}
				/>
				<Field
					name="gender"
					component={PortalSelectMenu}
					usage="portal"
					label={t('User_UserProfile_Label_Gender')}
					items={genderOptions}
					searchable={false}
				/>
				<Button className="formButton" type="submit">
					{t('BSPortal_Common_Save')}
				</Button>
			</>
		);
	}

	private renderReadOnlyProfile() {
		const { t, currentFormValues } = this.props;

		return (
			<dl className="list">
				<dt>{t('User_UserProfile_Label_ShortDescription')}</dt>
				<dd>{currentFormValues?.shortDescription}</dd>
			</dl>
		);
	}

	private renderEditableProfile() {
		const { t } = this.props;

		return (
			<>
				<Field
					name="shortDescription"
					component={PortalTextareaField}
					type="text"
					label={t('User_UserProfile_Label_ShortDescription')}
					validate={[maxLength250]}
				/>
				<Button className="formButton" type="submit">
					{t('BSPortal_Common_Save')}
				</Button>
			</>
		);
	}

	private openModal(type: keyof typeof modalToFieldsMap) {
		this.props.showModal('OnboardingQuizEdit', {
			closeModal: this.props.closeModal,
			showHeaderCloseButton: true,
			showModal: this.props.showModal,
			componentName: type,
			onSubmit: (result: IOnboardingQuizFormData) => this.onSubmitModal(result, type),
		});
	}

	private renderReadOnlyHair() {
		const { t, currentFormValues } = this.props;
		const brandsJoined = currentFormValues?.brands.map((key) => ' ' + t(`DV_Brands_${key}`)).toString();
		const hairProductsJoined = currentFormValues?.hairProducts
			.map((key) => ' ' + t(`DV_HairProduct_${key}`))
			.toString();
		const hairRelatedGoalJoined = currentFormValues?.goals.map((key) => ' ' + t(`DV_Goals_${key}`)).toString();

		return (
			<dl className="list">
				<dt>{t('User_UserProfile_Label_HairType')}</dt>
				<dd>
					<BasicButton onClick={() => this.openModal('HairType')}>
						{currentFormValues?.hairType ? (
							t(`DV_HairTypes_${currentFormValues?.hairType}`)
						) : (
							<span>{t('User_UserProfile_Label_NotSelected')}</span>
						)}
						{renderIcon('search_go')}
					</BasicButton>
				</dd>
				<dt>{t('User_UserProfile_Label_HairLength')}</dt>
				<dd>
					<BasicButton onClick={() => this.openModal('HairLength')}>
						{currentFormValues?.hairLength ? (
							t(`DV_HairLengths_${currentFormValues?.hairLength}`)
						) : (
							<span>{t('User_UserProfile_Label_NotSelected')}</span>
						)}
						{renderIcon('search_go')}
					</BasicButton>
				</dd>
				<dt>{t('User_UserProfile_Label_HairColor')}</dt>
				<dd>
					<BasicButton onClick={() => this.openModal('HairColor')}>
						{currentFormValues?.hairColor ? (
							t(`DV_HairColors_${currentFormValues?.hairColor}`)
						) : (
							<span>{t('User_UserProfile_Label_NotSelected')}</span>
						)}
						{renderIcon('search_go')}
					</BasicButton>
				</dd>
				<dt>{t('User_UserProfile_Label_ColoredHair')}</dt>
				<dd>
					<BasicButton onClick={() => this.openModal('HairColor')}>
						{currentFormValues?.coloredHair === '1' ? (
							t('BSPortal_Common_Yes')
						) : currentFormValues?.coloredHair === '0' ? (
							t('BSPortal_Common_No')
						) : (
							<span>{t('User_UserProfile_Label_NotSelected')}</span>
						)}
						{renderIcon('search_go')}
					</BasicButton>
				</dd>
				<dt>{t('User_UserProfile_Label_ScalpCondition')}</dt>
				<dd>
					<BasicButton onClick={() => this.openModal('ScalpCondition')}>
						{currentFormValues?.scalpMoisture ? (
							t(`DV_ScalpMoisture_${currentFormValues?.scalpMoisture}`)
						) : (
							<span>{t('User_UserProfile_Label_NotSelected')}</span>
						)}
						{renderIcon('search_go')}
					</BasicButton>
				</dd>
				<dt>{t('User_UserProfile_Label_HairProducts')}</dt>
				<dd>
					<BasicButton onClick={() => this.openModal('ScalpCondition')}>
						{hairProductsJoined ? hairProductsJoined : <span>{t('User_UserProfile_Label_NotSelected')}</span>}
						{renderIcon('search_go')}
					</BasicButton>
				</dd>
				<dt>{t('User_UserProfile_Label_Brands')}</dt>
				<dd>
					<BasicButton onClick={() => this.openModal('ProductBrand')}>
						{brandsJoined ? brandsJoined : <span>{t('User_UserProfile_Label_NotSelected')}</span>}
						{renderIcon('search_go')}
					</BasicButton>
				</dd>
				<dt>{t('User_UserProfile_Label_Goals')}</dt>
				<dd>
					<BasicButton onClick={() => this.openModal('HairRelatedGoal')}>
						{hairRelatedGoalJoined ? hairRelatedGoalJoined : <span>{t('User_UserProfile_Label_NotSelected')}</span>}
						{renderIcon('search_go')}
					</BasicButton>
				</dd>
			</dl>
		);
	}

	private renderReadOnlySkin() {
		const { t, currentFormValues } = this.props;

		const skinProblemsJoined = currentFormValues?.skinProblems
			.map((key) => ' ' + t(`DV_SkinProblems_${key}`))
			.toString();

		return (
			<dl className="list">
				<dt>{t('User_UserProfile_Label_SkinProblem')}</dt>
				<dd>
					<BasicButton onClick={() => this.openModal('SkinProblem')}>
						{skinProblemsJoined ? skinProblemsJoined : <span>{t('User_UserProfile_Label_NotSelected')}</span>}
						{renderIcon('search_go')}
					</BasicButton>
				</dd>
			</dl>
		);
	}

	private renderReadOnlyEyes() {
		const { t, currentFormValues } = this.props;

		return (
			<dl className="list">
				<dt>{t('User_UserProfile_Label_EyeColor')}</dt>
				<dd>
					<BasicButton onClick={() => this.openModal('EyeColor')}>
						{currentFormValues?.eyesColor ? (
							t(`DV_EyesColors_${currentFormValues?.eyesColor}`)
						) : (
							<span>{t('User_UserProfile_Label_NotSelected')}</span>
						)}
						{renderIcon('search_go')}
					</BasicButton>
				</dd>
				<dt>{t('User_UserProfile_Label_EyebrowType')}</dt>
				<dd>
					<BasicButton onClick={() => this.openModal('EyeColor')}>
						{currentFormValues?.eyebrowType ? (
							t(`DV_EyebrowTypes_${currentFormValues?.eyebrowType}`)
						) : (
							<span>{t('User_UserProfile_Label_NotSelected')}</span>
						)}
						{renderIcon('search_go')}
					</BasicButton>
				</dd>
			</dl>
		);
	}

	private renderReadOnlyNails() {
		const { t, currentFormValues } = this.props;

		return (
			<dl className="list">
				<dt>{t('User_UserProfile_Label_NailsShape')}</dt>
				<dd>
					<BasicButton onClick={() => this.openModal('NailsShape')}>
						{currentFormValues?.nailShape ? (
							t(`DV_NailShapes_${currentFormValues?.nailShape}`)
						) : (
							<span>{t('User_UserProfile_Label_NotSelected')}</span>
						)}
						{renderIcon('search_go')}
					</BasicButton>
				</dd>
			</dl>
		);
	}

	/** --------------RENDER ----------------------- */
	public render() {
		const { handleSubmit, t, formErrors, submitFailed, location, initialValues } = this.props;
		const errorSummaryProps = {
			formErrors,
			submitFailed,
			error: this.props.userProfile ? this.props.userProfile.error : false,
			message: this.props.userProfile ? this.props.userProfile.message : null,
			location,
			mapErrorKeyToMessage: this.mapErrorKeyToMessage,
		};

		return (
			<ManageUserWrapper user={initialValues}>
				<br />
				<div className="userProfileEditForm">
					<LoadingOverlayWrapper
						active={this.props.userProfile.isFetching}
						spinner={true}
						text={t('BSPortal_Common_Loading')}
						background="rgba(0, 0, 0, 0.1)"
					>
						<div className="profileError">
							<ErrorSummary {...errorSummaryProps} />
						</div>
						<form onSubmit={handleSubmit(this.onSubmit)} className="editForm">
							<Field name="id" component="input" type="hidden" />
							<PortalPageSection
								title={t('User_UserProfile_Bio')}
								readOnly={this.state.readOnlyBio}
								toggleEdit={this.toggleReadOnlyBio}
							>
								{this.state.readOnlyBio ? this.renderReadOnlyBio() : this.renderEditableBio()}
							</PortalPageSection>
							<PortalPageSection
								title={t('User_UserProfile_ProfileInfo')}
								readOnly={this.state.readOnlyProfile}
								toggleEdit={this.toggleReadOnlyProfile}
							>
								{this.state.readOnlyProfile ? this.renderReadOnlyProfile() : this.renderEditableProfile()}
							</PortalPageSection>
							<PortalPageSection title={t('User_UserProfile_Hair')} variant="dark">
								{this.renderReadOnlyHair()}
							</PortalPageSection>
							<PortalPageSection title={t('User_UserProfile_Skin')} variant="dark">
								{this.renderReadOnlySkin()}
							</PortalPageSection>
							<PortalPageSection title={t('User_UserProfile_Eyes')} variant="dark">
								{this.renderReadOnlyEyes()}
							</PortalPageSection>
							<PortalPageSection title={t('User_UserProfile_Nails')} variant="dark">
								{this.renderReadOnlyNails()}
							</PortalPageSection>
						</form>
					</LoadingOverlayWrapper>
				</div>
			</ManageUserWrapper>
		);
	}
}

let UserProfile = reduxForm({
	form: c.FORM_NAME_USERPROFILE,
	enableReinitialize: true,
	onSubmit: putUserProfile,
})(UserProfileForm);

UserProfile = connect(
	(state: IStore) => {
		let currentFormValues: IUserProfile = null;
		if (state.form[c.FORM_NAME_USERPROFILE] && state.form[c.FORM_NAME_USERPROFILE].values) {
			currentFormValues = state.form[c.FORM_NAME_USERPROFILE].values;
		} else {
			currentFormValues = state.userprofile.user;
		}

		const initialValues = state.userprofile.user ? { ...state.userprofile.user } : state.userprofile.user;

		if (initialValues && typeof initialValues.coloredHair === 'boolean') {
			initialValues.coloredHair = initialValues.coloredHair ? '1' : '0';
		}

		return {
			initialValues,
			userProfile: state.userprofile,
			userPreferences: state.userPreferences,
			currentFormValues,
			formErrors: getFormSyncErrors(c.FORM_NAME_USERPROFILE)(state),
			baseUrlClient: state.fetchDefault.baseUrlClient,
			countryCallingCodes: state.domainValues.countryCallingCodes,
		};
	},
	(dispatch) => ({
		putUserProfile: (form: IUserProfile) => dispatch(putUserProfile(form)),
		change: (field: string, value: any) => dispatch(change(field, value)),
		showModal: (type: string, modalProps: any) => dispatch(showModal(type, modalProps)),
		closeModal: () => dispatch(closeModal()),
		deactivateUser: () => dispatch(deactivateUser()),
		getUserProfile: () => dispatch(getUserProfile()),
		notifSend: (data: any) => dispatch(notifSend(data)),
		sendVerificationEmail: () => dispatch(sendVerificationEmail()),
		getCountryCallingCodeDomainValues: () => dispatch(domainValuesMap.countryCallingCodes()),
	}),
)(UserProfile);

export { UserProfile };
